import React, { Suspense, useEffect, useState } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBacterium } from '@fortawesome/free-solid-svg-icons/faBacterium';
import { faBorderAll } from '@fortawesome/free-solid-svg-icons/faBorderAll';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import { faCube } from '@fortawesome/free-solid-svg-icons/faCube';
import { faFlask } from '@fortawesome/free-solid-svg-icons/faFlask';
import { faHouse } from '@fortawesome/free-solid-svg-icons/faHouse';
import { faNewspaper } from '@fortawesome/free-solid-svg-icons/faNewspaper';
import { faTableTennisPaddleBall } from '@fortawesome/free-solid-svg-icons/faTableTennisPaddleBall';
import { faVirus } from '@fortawesome/free-solid-svg-icons/faVirus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Loading } from '../components';

import './Projects.css';

const ColorWheel = React.lazy(() => import('./ColorWheel'));
const GridDrawer = React.lazy(() => import('./GridDrawer'));
const Peter = React.lazy(() => import('./PeterThe3DPrinter'));
const Pong = React.lazy(() => import('./Pong'));
const Slime = React.lazy(() => import('./Slime'));
const Brightside = React.lazy(() => import('./Brightside'));
const GameOfLife = React.lazy(() => import('./GameOfLife'));
const Viver = React.lazy(() => import('./Viver'));

/** Defines all the projects we have available, and points to the lazy loaded component for it. */
const PROJECT_MAPPING = {
    colorWheel: <ColorWheel />,
    pong: <Pong />,
    grid: <GridDrawer />,
    peter: <Peter />,
    brightside: <Brightside />,
    slime: <Slime />,
    gol: <GameOfLife />,
    viver: <Viver />,
};

export type ProjectName = keyof typeof PROJECT_MAPPING;
const isProjectName = (name: string) => name in PROJECT_MAPPING;
export const toProjectName = (name: string): ProjectName => {
    if (!isProjectName(name)) {
        return 'slime';
    }
    return name as ProjectName;
};

interface _BaseProject {
    name: ProjectName;
    label: string;
    icon: IconDefinition;
}

interface PureProject extends _BaseProject {
    type: 'pure';
}

interface ThreeProject extends _BaseProject {
    type: 'three';
}

type Project = PureProject | ThreeProject;

const PURE_PROJECTS: PureProject[] = [
    { type: 'pure', name: 'colorWheel', label: 'Color', icon: faCircle },
    { type: 'pure', name: 'pong', label: 'Pong', icon: faTableTennisPaddleBall },
    { type: 'pure', name: 'grid', label: 'Grid', icon: faBorderAll },
    { type: 'pure', name: 'peter', label: 'Peter', icon: faCube },
    { type: 'pure', name: 'brightside', label: 'Brightside', icon: faNewspaper },
    { type: 'pure', name: 'gol', label: 'GoL', icon: faBacterium },
    { type: 'pure', name: 'viver', label: 'Viver', icon: faFlask },
];

const THREE_PROJECTS: ThreeProject[] = [
    { type: 'three', name: 'slime', label: 'Slime', icon: faVirus },
];

function Projects({
    projectFromUrl,
    navigate,
}: {
    projectFromUrl: ProjectName;
    navigate: (destination: string) => void;
}) {
    const [selectedProject, setSelectedProject] = useState<ProjectName>(projectFromUrl);

    // Determine if this is needed.
    useEffect(() => {
        setSelectedProject(projectFromUrl);
    }, [projectFromUrl]);

    const handleProjectSelect = (project: ProjectName) => {
        navigate(`/projects/${project}`);
        setSelectedProject(project);
    };

    return (
        <div className="projects-container">
            <Sidebar
                selectedProject={selectedProject}
                handleProjectSelect={handleProjectSelect}
                navigate={navigate}
            />
            <Suspense fallback={<Loading />} key={selectedProject}>
                <div className="projects-content">{PROJECT_MAPPING[selectedProject]}</div>
            </Suspense>
        </div>
    );
}

interface SidebarProps {
    selectedProject: ProjectName | null;
    handleProjectSelect: (project: ProjectName) => void;
    navigate: (destination: string) => void;
}

function Sidebar({ selectedProject, handleProjectSelect, navigate }: SidebarProps) {
    const renderSelectProjectButton = (project: Project) => (
        <button
            key={project.name}
            className={`link sidebar-link ${selectedProject === project.name ? 'active' : ''}`}
            onClick={() => handleProjectSelect(project.name)}
        >
            <FontAwesomeIcon className="icon" icon={project.icon} />
            <span className="label">{project.label}</span>
        </button>
    );

    return (
        <div className="sidebar">
            <button onClick={() => navigate('')} className="link home-link purple">
                <FontAwesomeIcon className="icon" icon={faHouse} />
                <span className="label">Home</span>
            </button>

            {PURE_PROJECTS.map((project) => renderSelectProjectButton(project))}
            {THREE_PROJECTS.map((project) => renderSelectProjectButton(project))}
        </div>
    );
}

export default Projects;
